import { useEffect } from "react";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

export const Drawer = ({ 
  isOpen,
  onClose,
  title,
  children,
  width = 'md',
  className 
}) => {
  // Close on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);
  
  const widthClasses = {
    sm: 'w-full sm:w-80',
    md: 'w-full sm:w-96',
    lg: 'w-full sm:w-[480px]',
    xl: 'w-full sm:w-[600px]',
  };
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className={cn(
          "fixed inset-0 z-50 bg-obsidian/60 backdrop-blur-sm transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
        data-testid="drawer-backdrop"
      />
      
      {/* Drawer Panel */}
      <div 
        className={cn(
          "fixed top-0 right-0 z-50 h-full bg-graphite border-l border-white/[0.06]",
          "shadow-2xl shadow-black/50 transition-transform duration-300 ease-out",
          widthClasses[width],
          isOpen ? "translate-x-0" : "translate-x-full",
          className
        )}
        data-testid="drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-white/[0.06]">
          <h2 className="text-lg sm:text-xl font-semibold text-cloud-white truncate pr-4">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/[0.05] transition-colors duration-200 shrink-0"
            data-testid="drawer-close"
          >
            <X className="w-5 h-5 text-soft-silver" />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-4 sm:p-5 h-[calc(100%-65px)] sm:h-[calc(100%-73px)] overflow-y-auto">
          {children}
        </div>
      </div>
    </>
  );
};

export default Drawer;
